/**
 * AppLayout Component - Responsive application shell
 *
 * Provides the main responsive layout wrapper for the OwnYou consumer app.
 * - Mobile/Tablet (<1024px): TopBar + content + floating BottomTabBar pill
 * - Desktop (>=1024px): DesktopSidebar + TopBar (without logo) + content
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@ownyou/ui-design-system';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { ChatScaffoldModal } from '@/components/chat/ChatScaffoldModal';
import { TopBar } from './TopBar';
import { BottomTabBar } from './BottomTabBar';
import { DesktopSidebar } from './DesktopSidebar';

export interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const { showSidebar } = useBreakpoint();
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const openChat = () => setChatOpen(true);
    window.addEventListener('ownyou:open-chat', openChat);
    return () => window.removeEventListener('ownyou:open-chat', openChat);
  }, []);

  if (location.pathname.startsWith('/__qa/')) {
    return (
      <div
        data-testid="app-layout"
        className="min-h-screen"
        style={{
          background: 'var(--aurora-bg)',
          color: 'var(--aurora-ink)',
          fontFamily: 'var(--aurora-font-ui)',
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      data-testid="app-layout"
      className="min-h-screen"
      style={{
        background: 'var(--aurora-bg)',
        color: 'var(--aurora-ink)',
        fontFamily: 'var(--aurora-font-ui)',
      }}
    >
      {showSidebar ? (
        /* Desktop layout: Sidebar + TopBar (no logo) + content */
        <div className="flex h-screen">
          <DesktopSidebar onOpenChat={() => setChatOpen(true)} />

          <div className="flex flex-col flex-1 min-w-0">
            <TopBar showLogo={false} />

            <main className="flex-1 overflow-y-auto p-6">
              {children}
            </main>
          </div>
        </div>
      ) : (
        /* Mobile/Tablet layout: TopBar + content + floating BottomTabBar pill */
        <div className="flex flex-col min-h-screen">
          <TopBar showLogo={true} />

          <main
            className={cn(
              'flex-1 px-4 pt-2',
              'pb-28', /* Extra bottom padding to clear the floating pill nav */
            )}
          >
            {children}
          </main>

          <BottomTabBar />
        </div>
      )}
      <ChatScaffoldModal isOpen={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
}
